"use client";

/**
 * Card Grid Card Display
 * Renders a single card in the card grid section.
 * Supports three interaction modes: none (static), link (entire card clickable), button (CTA).
 */

import type React from "react";
import { OpenNewWindow } from "iconoir-react";
import { renderIconEmoji } from "@/fetch-ui/icon-emoji-data";
import { getButtonTextColor } from "@/features/landing-pages/_logic/color-utils";
import type { CardGridItem } from "../../../_types";
import { isTestableUrl } from "../../../_logic/utils";
import { useCanvasFieldClick } from "../../../_hooks/useCanvasFieldClick";
import { useBuilderContext } from "../../../_hooks/useBuilderContext";
import { usePublicViewContext } from "../../public/PublicViewContext";
import { SmartButton } from "../../public/SmartButton";
import { SmartLink } from "../../public/SmartLink";
import { SanitizedHtml } from "./_shared/SanitizedHtml";

interface CardGridCardDisplayProps {
  card: CardGridItem;
  componentId: string;
}

/** Renders a single card in the card grid */
export function CardGridCardDisplay({ card, componentId }: CardGridCardDisplayProps): React.ReactElement {
  const { primaryColor, eventId } = useBuilderContext();
  const { isPublic } = usePublicViewContext();
  const fieldClick = useCanvasFieldClick(componentId);
  const isNone = card.interactionType === "none";
  const isLink = card.interactionType === "link";
  const isButton = card.interactionType === "button";
  const safeHref = card.href && isTestableUrl(card.href) ? card.href : undefined;
  const buttonTextColor = getButtonTextColor(primaryColor);

  const cardContent = (
    <>
      {/* External link icon for link cards */}
      {isLink && (
        <OpenNewWindow className="absolute right-4 top-4 h-4 w-4 text-gray-400" strokeWidth={1.75} aria-hidden="true" />
      )}

      {/* Icon / Emoji */}
      {card.icon && <div className="mb-1">{renderIconEmoji(card.icon)}</div>}

      {/* Title */}
      <h3 className="text-lg font-semibold text-gray-900">{card.title || "Untitled"}</h3>

      {/* Description */}
      {card.description && (
        <SanitizedHtml
          html={card.description}
          className="prose prose-sm prose-gray max-w-none text-gray-600 prose-a:text-[var(--lp-accent-on-card)] prose-a:underline"
        />
      )}
    </>
  );

  // No interaction — static card
  if (isNone) {
    return (
      <div
        className={`relative flex flex-col gap-2 rounded-xl border border-gray-200 bg-white p-5 ${!isPublic ? "cursor-pointer transition-shadow hover:shadow-md" : ""}`}
        onClick={!isPublic ? fieldClick(`cardgrid-item:${card.id}`) : undefined}
      >
        {cardContent}
      </div>
    );
  }

  // Link card — public view routes through SmartLink (form modal / anchor / external / nav), builder is just a div
  if (isLink) {
    if (isPublic && safeHref) {
      return (
        <SmartLink
          href={safeHref}
          eventId={eventId}
          primaryColor={primaryColor}
          className="relative flex flex-col gap-2 rounded-xl border border-gray-200 bg-white p-5 text-left transition-shadow hover:shadow-md"
          ariaLabel={card.title || "Untitled"}
        >
          {cardContent}
        </SmartLink>
      );
    }

    return (
      <div
        className={`relative flex flex-col gap-2 rounded-xl border border-gray-200 bg-white p-5 ${!isPublic ? "cursor-pointer transition-shadow hover:shadow-md" : ""}`}
        onClick={!isPublic ? fieldClick(`cardgrid-item:${card.id}`) : undefined}
      >
        {cardContent}
      </div>
    );
  }

  // Button card
  return (
    <div
      className={`relative flex flex-col justify-between gap-2 rounded-xl border border-gray-200 bg-white p-5 ${!isPublic ? "cursor-pointer transition-shadow hover:shadow-md" : ""}`}
      onClick={!isPublic ? fieldClick(`cardgrid-item:${card.id}`) : undefined}
    >
      <div className="flex flex-col gap-2">{cardContent}</div>
      {isButton && card.buttonLabel && (
        <div className="mt-3">
          {isPublic && safeHref ? (
            <SmartButton
              href={safeHref}
              label={card.buttonLabel}
              linkedSteps={card.linkedSteps}
              primaryColor={primaryColor}
              eventId={eventId}
              className="inline-flex items-center rounded-xl px-4 py-2 text-sm font-semibold transition-opacity [@media(hover:hover)]:hover:opacity-90"
              style={{ backgroundColor: primaryColor, color: buttonTextColor }}
            />
          ) : (
            <span
              className="inline-block rounded-xl px-4 py-2 text-sm font-semibold"
              style={{ backgroundColor: primaryColor, color: buttonTextColor }}
            >
              {card.buttonLabel}
            </span>
          )}
        </div>
      )}
    </div>
  );
}
